import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import {
  isCompanyCodeFormatValid,
  COMPANY_CODE_FORMAT_MESSAGE,
  FIX_HIGHLIGHTED_FIELDS,
  isValidEmail,
  step5ValidationMessage,
  step6ValidationMessage,
} from "./OnboardingCapture.utils";
import { validateStep2 } from "./Step2/Step2.validation";
import {
  FRANCHISE_REQUIRED_MESSAGE,
  step3ClinicMessage,
} from "../ImperansReference/ImperansReference.rules";

/**
 * A step's own advance validation, with the message each story specifies. The shared completion
 * engine on the server decides completion; these messages must never disagree with it, only say
 * more precisely what is missing (S1.5, S5.10, S6.1).
 *
 * Returns null when the step's own rules pass — the shell then falls back to the server's verdict.
 */
/**
 * Whether the client holds a Client Type, which step 1 requires (ruled 9 Sep 2026, task 6007).
 *
 * Either store counts, matching the server's own rule: Add New Client writes the type it was given
 * to the CRM's CompanyType and never to the onboarding array (S1.8), so reading only the array
 * would refuse to advance every client created that way — each of which does hold a client type.
 */
const hasClientType = (state: IOnboardingWizardState): boolean =>
  Boolean(state.clientInformation?.clientType?.trim()) || Boolean(state.companyType?.trim());

export const stepValidationMessage = (
  state: IOnboardingWizardState | null,
  step: number,
): string | null => {
  if (!state) return null;

  switch (step) {
    case 1: {
      const missing =
        !state.companyName?.trim() ||
        !state.companyCode?.trim() ||
        !state.franchiseID ||
        !state.countryCode ||
        !state.primaryRelationshipManagerUser ||
        !hasClientType(state) ||
        // Imperans refuses a client without one of its industry codes (self-check S6), so the step
        // that captures it cannot read complete without it.
        !state.industryID ||
        // The owning Imperans franchise, picked from the paired Imperans's list (story 6372).
        !state.imperansFranchiseID;
      if (missing) return FIX_HIGHLIGHTED_FIELDS;
      if (!isCompanyCodeFormatValid(state.companyCode)) return COMPANY_CODE_FORMAT_MESSAGE;
      return null;
    }

    // Step 2's own conditions, in the order story 5629 states them.
    case 2:
      return validateStep2(
        state.services,
        state.references.step2Context,
        {
          method: (key) =>
            state.references.step2Methods.find((x) => x.key === key)?.label ?? key,
          occasion: (key) =>
            [...(state.references.step2HairTestReasons || []), ...(state.references.step2TestReasons || [])]
              .find((x) => x.key === key)?.label ?? key,
        },
        state.healthTickReview,
        state.references,
      );

    // At least one site, then every site linked to the Imperans clinic that services it (story 6373).
    case 3:
      if (!state.sites.length) return "Add at least one site before continuing.";
      return step3ClinicMessage(state.sites);

    // The order S4.8 states, first failure shown. The shared mailbox never satisfies the
    // results-access rule, and an invalid mailbox address blocks the step (story 5607).
    case 4: {
      if (!state.contacts.length) return "Add at least one contact before continuing.";
      if (!state.contacts.some((x) => x.isRequestingTestPersonnel))
        return "Mark one contact as the Report to Person (RTP).";
      if (!state.contacts.some((x) => x.hasResultsAccess))
        return "At least one contact needs to receive results.";
      if (
        state.contactSettings?.genericMailboxEnabled &&
        !isValidEmail(state.contactSettings.genericMailboxAddress)
      )
        return FIX_HIGHLIGHTED_FIELDS;
      return null;
    }

    case 5:
      return step5ValidationMessage(
        state.invoicing,
        state.invoicing?.arrangement === "Other",
      );

    case 6:
      return step6ValidationMessage(state);

    default:
      return null;
  }
};
/**
 * The step-1 fields that are blocking the advance, keyed by field, each with the reason to print
 * beneath that field (bug 5668).
 *
 * "Please fix the highlighted fields" is only honest if something is actually highlighted. Step 1 is
 * a hand-rolled form, so its required fields carried an asterisk but nothing ever marked them or
 * said why a refusal happened — the only cue was a group label changing colour, which reads as
 * ordinary styling. This gives the step exactly what it needs to mark the offending fields.
 *
 * Empty when nothing is blocking, so a caller can treat it as the pass/fail answer too.
 */
export const step1FieldErrors = (
  state: IOnboardingWizardState | null,
): Record<string, string> => {
  if (!state) return {};

  const errors: Record<string, string> = {};

  if (!state.companyName?.trim()) errors.companyName = "Enter the company name.";
  if (!state.franchiseID) errors.franchiseID = "Choose the TDDA supporting region.";
  if (!state.countryCode) errors.countryCode = "Choose the country.";
  if (!state.primaryRelationshipManagerUser)
    errors.primaryRelationshipManager = "Choose the TDDA primary relationship manager.";

  if (!state.companyCode?.trim()) errors.companyCode = "Enter or generate a company code.";
  else if (!isCompanyCodeFormatValid(state.companyCode))
    errors.companyCode = COMPANY_CODE_FORMAT_MESSAGE;

  if (!hasClientType(state)) errors.clientType = "Choose the client type.";

  if (!state.industryID) errors.industryID = "Choose the industry.";

  if (!state.imperansFranchiseID) errors.owningFranchise = FRANCHISE_REQUIRED_MESSAGE;

  return errors;
};
